import { useEffect, useState, useRef } from "react";
import "./PaymentModal.scss";

export default function PaymentModal({
  open,
  loading = false,
  onClose,
  onSubmit,
  orderSum = 0,
  paidSum = 0,
  tr = (s) => s,
}) {
  const remaining = Math.max(0, Number(orderSum) - Number(paidSum));
  const [amount, setAmount] = useState("");
  const overlayRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Set default amount to 100% (remaining)
      setAmount(String(remaining));
      // Focus input when modal opens
      setTimeout(() => {
        inputRef.current?.focus();
        // Select all text for easy editing
        inputRef.current?.select();
      }, 300);
      // Prevent body scroll
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      // Reset amount when modal closes
      setAmount("");
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, remaining]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && open && !loading) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, loading, onClose]);

  const handleSubmit = () => {
    const amountNum = Number(amount);
    if (!isFinite(amountNum) || amountNum <= 0) {
      return;
    }
    if (amountNum > remaining) {
      setAmount(String(remaining));
      return;
    }
    onSubmit(amountNum);
  };

  const handleQuickFill = (percent) => {
    const quickAmount = Math.floor((remaining * percent) / 100);
    setAmount(String(quickAmount));
    inputRef.current?.focus();
  };

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="payment-modal__overlay"
      onClick={(e) => {
        if (e.target === overlayRef.current && !loading) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="payment-modal__content">
        {/* Header */}
        <div className="payment-modal__header">
          <h2 className="payment-modal__title">{tr("Оплата")}</h2>
          <button
            type="button"
            className="payment-modal__close-btn"
            onClick={() => !loading && onClose()}
            aria-label={tr("Закрыть")}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {/* Summary */}
        <div className="payment-modal__summary">
          <div className="payment-modal__summary-row">
            <span className="payment-modal__summary-label">
              {tr("Сумма по заказам")}
            </span>
            <span className="payment-modal__summary-value">
              {Number(orderSum).toLocaleString()}
            </span>
          </div>
          <div className="payment-modal__summary-row">
            <span className="payment-modal__summary-label">
              {tr("Оплачено")}
            </span>
            <span className="payment-modal__summary-value">
              {Number(paidSum).toLocaleString()}
            </span>
          </div>
          <div className="payment-modal__summary-row">
            <span className="payment-modal__summary-label">
              {tr("Остаток")}
            </span>
            <span className="payment-modal__summary-value payment-modal__summary-value--large">
              {remaining.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Form */}
        <div className="payment-modal__form-group">
          <label className="payment-modal__label">
            {tr("Введите сумму")}
          </label>
          <input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            min="0"
            max={remaining}
            step="1"
            className="payment-modal__input"
            placeholder={String(remaining)}
            value={amount}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || (!isNaN(value) && Number(value) >= 0)) {
                setAmount(value);
              }
            }}
            disabled={loading}
          />

          {/* Quick fill buttons */}
          {remaining > 0 && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "12px",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => handleQuickFill(25)}
                disabled={loading}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  background: "#ffffff",
                  color: "#374151",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                25%
              </button>
              <button
                type="button"
                onClick={() => handleQuickFill(50)}
                disabled={loading}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  background: "#ffffff",
                  color: "#374151",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                50%
              </button>
              <button
                type="button"
                onClick={() => handleQuickFill(100)}
                disabled={loading}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  background: "#ffffff",
                  color: "#374151",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {tr("Все")}
              </button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="payment-modal__actions">
          <button
            type="button"
            className="payment-modal__button payment-modal__button--cancel"
            onClick={onClose}
            disabled={loading}
          >
            {tr("Отмена")}
          </button>
          <button
            type="button"
            className="payment-modal__button payment-modal__button--submit"
            onClick={handleSubmit}
            disabled={loading || !amount || Number(amount) <= 0}
          >
            {loading ? (
              <>
                <span className="payment-modal__loading"></span>
                {tr("Подождите...")}
              </>
            ) : (
              <>
                <i className="fas fa-credit-card"></i>
                {tr("Оплатить")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
